export type BuiltInPlanId = 'free' | 'pro' | 'chamber' | 'enterprise';
export type PlanId = BuiltInPlanId | `custom_${string}`;

/** Numeric cap or unlimited */
export type LimitValue = number | 'unlimited';

export type TemplateAccessTier = 'basic' | 'standard' | 'full';

export type BillingCycle = 'monthly' | 'annual';

export interface PlanEntitlements {
  /** AI-assisted drafts per calendar month */
  aiDraftsPerMonth: LimitValue;
  /** New draft documents per month (excludes templates) */
  documentsPerMonth: LimitValue;
  /** Workspace storage cap in megabytes */
  storageMb: LimitValue;
  /** Which template library tier is visible */
  templatesAccess: TemplateAccessTier;
  /** LexCounsel chat / AI pane */
  lexCounselEnabled: boolean;
  /** Multi-lawyer workspace features */
  teamWorkspaces: boolean;
  /** Firm custom template authoring */
  customTemplates: boolean;
  /** Case import / advanced analytics */
  advancedAnalytics: boolean;
  /** Active cases allowed per workspace */
  casesPerWorkspace: LimitValue;
  /** Users included in base plan price */
  includedUsers: number;
  /** Maximum users (seats) on account */
  maxUsers: LimitValue;
  /** Max workspaces (billing organizations) per account */
  maxWorkspaces: LimitValue;
  /** Price per additional user beyond included (PKR/month) */
  pricePerAdditionalUser: number;
  /** Workflow automation features */
  workflowAutomation: boolean;
  /** Advanced workspace permission controls */
  advancedPermissions: boolean;
  /** Tenant audit log access */
  auditLogs: boolean;
  /** SSO login (Enterprise) */
  ssoEnabled: boolean;
  /** API access (Enterprise) */
  apiAccess: boolean;
}

export type SubscriptionStatus =
  | 'active'
  | 'paused'
  | 'canceled'
  | 'past_due'
  | 'trialing';

export type PaymentStatus = 'succeeded' | 'failed' | 'pending' | 'refunded';

export interface PlanDefinition {
  id: string;
  name: string;
  priceMonthly: number;
  priceAnnual?: number;
  currency: string;
  features: string[];
  entitlements: PlanEntitlements;
  /** When false, plan is hidden from self-serve upgrade (admin assign only) */
  enabled: boolean;
  isCustom?: boolean;
}

export interface PlanCatalogDoc {
  plans: PlanDefinition[];
  updatedAt?: string;
}

export interface PlatformSubscription {
  id: string;
  userId: string;
  accountId?: string;
  userEmail: string;
  userName?: string;
  plan: PlanId;
  status: SubscriptionStatus;
  mrr: number;
  currency: string;
  billingCycle?: BillingCycle;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  createdAt: string;
  updatedAt: string;
  source: 'admin' | 'stripe' | 'user_sync';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  pausedAt?: string;
  canceledAt?: string;
}

export interface PlatformPayment {
  id: string;
  userId: string;
  accountId?: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  plan?: PlanId;
  subscriptionId?: string;
  provider: 'manual' | 'stripe';
  stripeInvoiceId?: string;
  createdAt: string;
  updatedAt?: string;
  refundedAt?: string;
  failureReason?: string;
  note?: string;
}

export interface RevenueAnalytics {
  mrr: number;
  arr: number;
  activeSubscriptions: number;
  paidUsers: number;
  churnRate: number;
  retentionRate: number;
  revenuePerUser: number;
  failedPaymentsCount: number;
  revenueByPlan: { plan: PlanId; count: number; mrr: number }[];
  revenueHistory: { month: string; label: string; value: number }[];
  churnHistory: { month: string; label: string; value: number }[];
}

export const DEFAULT_ENTITLEMENTS: Record<BuiltInPlanId, PlanEntitlements> = {
  free: {
    aiDraftsPerMonth: 5,
    documentsPerMonth: 10,
    storageMb: 500,
    templatesAccess: 'basic',
    lexCounselEnabled: false,
    teamWorkspaces: false,
    customTemplates: false,
    advancedAnalytics: false,
    casesPerWorkspace: 50,
    includedUsers: 1,
    maxUsers: 1,
    maxWorkspaces: 1,
    pricePerAdditionalUser: 0,
    workflowAutomation: false,
    advancedPermissions: false,
    auditLogs: false,
    ssoEnabled: false,
    apiAccess: false,
  },
  pro: {
    aiDraftsPerMonth: 'unlimited',
    documentsPerMonth: 'unlimited',
    storageMb: 2048,
    templatesAccess: 'full',
    lexCounselEnabled: true,
    teamWorkspaces: false,
    customTemplates: false,
    advancedAnalytics: true,
    casesPerWorkspace: 'unlimited',
    includedUsers: 1,
    maxUsers: 6,
    maxWorkspaces: 5,
    pricePerAdditionalUser: 1000,
    workflowAutomation: false,
    advancedPermissions: false,
    auditLogs: false,
    ssoEnabled: false,
    apiAccess: false,
  },
  chamber: {
    aiDraftsPerMonth: 'unlimited',
    documentsPerMonth: 'unlimited',
    storageMb: 5120,
    templatesAccess: 'full',
    lexCounselEnabled: true,
    teamWorkspaces: true,
    customTemplates: true,
    advancedAnalytics: true,
    casesPerWorkspace: 'unlimited',
    includedUsers: 6,
    maxUsers: 'unlimited',
    maxWorkspaces: 'unlimited',
    pricePerAdditionalUser: 1000,
    workflowAutomation: true,
    advancedPermissions: true,
    auditLogs: false,
    ssoEnabled: false,
    apiAccess: false,
  },
  enterprise: {
    aiDraftsPerMonth: 'unlimited',
    documentsPerMonth: 'unlimited',
    storageMb: 10240,
    templatesAccess: 'full',
    lexCounselEnabled: true,
    teamWorkspaces: true,
    customTemplates: true,
    advancedAnalytics: true,
    casesPerWorkspace: 'unlimited',
    includedUsers: 6,
    maxUsers: 'unlimited',
    maxWorkspaces: 'unlimited',
    pricePerAdditionalUser: 0,
    workflowAutomation: true,
    advancedPermissions: true,
    auditLogs: true,
    ssoEnabled: true,
    apiAccess: true,
  },
};

export const BUILT_IN_PLAN_PRICES: Record<
  BuiltInPlanId,
  { priceMonthly: number; priceAnnual: number; label: string }
> = {
  free: { priceMonthly: 0, priceAnnual: 0, label: 'Free' },
  pro: { priceMonthly: 4999, priceAnnual: 49990, label: 'Pro' },
  chamber: { priceMonthly: 14999, priceAnnual: 149990, label: 'Chamber' },
  enterprise: { priceMonthly: 0, priceAnnual: 0, label: 'Enterprise' },
};

function featureBullets(e: PlanEntitlements): string[] {
  const lim = (v: LimitValue, label: string) =>
    v === 'unlimited' ? `Unlimited ${label}` : `${v} ${label}`;
  return [
    lim(e.aiDraftsPerMonth, 'AI drafts / month'),
    lim(e.documentsPerMonth, 'documents / month'),
    lim(e.casesPerWorkspace, 'cases / workspace'),
    lim(e.storageMb, 'MB storage'),
    `${e.includedUsers} included user${e.includedUsers === 1 ? '' : 's'}`,
    lim(e.maxWorkspaces, 'workspaces'),
    `${e.templatesAccess} templates`,
    e.lexCounselEnabled ? 'LexCounsel AI' : 'No LexCounsel AI',
    ...(e.teamWorkspaces ? ['Team workspaces'] : []),
    ...(e.customTemplates ? ['Custom templates'] : []),
    ...(e.workflowAutomation ? ['Workflow automation'] : []),
    ...(e.advancedPermissions ? ['Advanced permissions'] : []),
    ...(e.auditLogs ? ['Audit logs'] : []),
    ...(e.ssoEnabled ? ['SSO'] : []),
    ...(e.apiAccess ? ['API access'] : []),
  ];
}

export const DEFAULT_PLAN_CATALOG: PlanDefinition[] = (
  ['free', 'pro', 'chamber', 'enterprise'] as BuiltInPlanId[]
).map((id) => ({
  id,
  name: BUILT_IN_PLAN_PRICES[id].label,
  priceMonthly: BUILT_IN_PLAN_PRICES[id].priceMonthly,
  priceAnnual: BUILT_IN_PLAN_PRICES[id].priceAnnual,
  currency: 'PKR',
  entitlements: DEFAULT_ENTITLEMENTS[id],
  enabled: id !== 'enterprise',
  features: featureBullets(DEFAULT_ENTITLEMENTS[id]),
}));

export function formatStorageLimitMb(mb: LimitValue): string {
  if (mb === 'unlimited') return 'Unlimited';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return Number.isInteger(gb) ? `${gb} GB` : `${gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

export function planMrr(plan: string, catalog: PlanDefinition[]): number {
  return catalog.find((p) => p.id === plan)?.priceMonthly ?? 0;
}

export function normalizePlanDefinition(raw: Partial<PlanDefinition> & { id: string }): PlanDefinition {
  const builtIn = DEFAULT_ENTITLEMENTS[raw.id as BuiltInPlanId];
  const base = builtIn ?? DEFAULT_ENTITLEMENTS.free;
  const entitlements: PlanEntitlements = {
    ...base,
    ...(raw.entitlements ?? {}),
  };
  const prices = BUILT_IN_PLAN_PRICES[raw.id as BuiltInPlanId];
  return {
    id: raw.id,
    name: raw.name ?? prices?.label ?? raw.id,
    priceMonthly: raw.priceMonthly ?? prices?.priceMonthly ?? 0,
    priceAnnual: raw.priceAnnual ?? prices?.priceAnnual,
    currency: raw.currency ?? 'PKR',
    entitlements,
    enabled: raw.enabled !== false,
    isCustom: raw.isCustom ?? raw.id.startsWith('custom_'),
    features: raw.features?.length ? raw.features : featureBullets(entitlements),
  };
}

/** Map legacy enterprise-only users to chamber during migration */
export function normalizeLegacyPlanId(planId: string): BuiltInPlanId | string {
  const raw = planId.toLowerCase();
  if (raw === 'paid') return 'pro';
  if (raw === 'enterprise') return 'chamber';
  return raw;
}

export function formatPlanLabel(planId: string): string {
  if (planId === 'free') return 'Free';
  if (planId === 'pro') return 'Pro';
  if (planId === 'chamber') return 'Chamber';
  if (planId === 'enterprise') return 'Enterprise';
  return planId.replace(/^custom_/, 'Custom ');
}
